import { FirebaseAuthService } from './firebase-auth';
import { HttpController } from './http';
import { Log } from './log';
import { Workshop, Registration, PaymentOption, User } from '../interfaces/application';
import { App } from './app-state';

export class AppApiController extends HttpController {
  private baseUrl: string = '';

  initialize(baseUrl: string): void {
    this.baseUrl = baseUrl;
    Log.debug(`AppApiService: initialized with base URL ${baseUrl}`);
  }

  async login(email: string, password: string): Promise<User> {
    const firebaseUser = await FirebaseAuthService.signIn(email, password);
    const user: User = {
      id: firebaseUser.uid,
      name: firebaseUser.displayName || firebaseUser.email || '',
      email: firebaseUser.email || '',
    };
    await App.setState('user', user);
    return user;
  }

  async logout(): Promise<void> {
    await FirebaseAuthService.signOut();
    await FirebaseAuthService.signInAnonymously();
    await App.setState('user', null);
  }

  private async authHeaders(): Promise<Record<string, string>> {
    await FirebaseAuthService.waitForSignIn();
    const token = await FirebaseAuthService.getIdToken();
    if (!token) {
      Log.error('AppApiService: No auth token available');
      return {};
    }
    return { Authorization: `Bearer ${token}` };
  }

  // ─── Workshops ─────────────────────────────────────────────────────────────

  async getWorkshops(): Promise<Workshop[]> {
    try {
      const headers = await this.authHeaders();
      const res = await this.httpGet(`${this.baseUrl}/workshops`, { headers });
      return (res.data as Workshop[]) ?? [];
    } catch (error) {
      Log.error(`AppApiService: getWorkshops failed: ${error.message}`);
      return [];
    }
  }

  async getWorkshopById(id: string): Promise<Workshop | null> {
    try {
      const headers = await this.authHeaders();
      const res = await this.httpGet(`${this.baseUrl}/workshops/${id}`, { headers });
      return res.status === 200 ? (res.data as Workshop) : null;
    } catch (error) {
      Log.error(`AppApiService: getWorkshopById failed: ${error.message}`);
      return null;
    }
  }

  // ─── Registrations ─────────────────────────────────────────────────────────

  async getRegistrationById(id: string): Promise<Registration | null> {
    try {
      const headers = await this.authHeaders();
      const res = await this.httpGet(`${this.baseUrl}/registrations/${id}`, { headers });
      return res.status === 200 ? (res.data as Registration) : null;
    } catch (error) {
      Log.error(`AppApiService: getRegistrationById failed: ${error.message}`);
      return null;
    }
  }

  async lookupRegistrationByKey(key: string): Promise<{ id: string } | null> {
    try {
      const headers = await this.authHeaders();
      const res = await this.httpGet(`${this.baseUrl}/registrations/lookup`, { headers, params: { key } });
      return res.status === 200 ? (res.data as { id: string } | null) : null;
    } catch (error) {
      Log.error(`AppApiService: lookupRegistrationByKey failed: ${error.message}`);
      return null;
    }
  }

  async createRegistration(data: Omit<Registration, 'id'>): Promise<Registration | null> {
    try {
      const headers = await this.authHeaders();
      const res = await this.httpPost(`${this.baseUrl}/registrations`, data, { headers });
      return res.status === 201 ? (res.data as Registration) : null;
    } catch (error) {
      Log.error(`AppApiService: createRegistration failed: ${error.message}`);
      return null;
    }
  }

  async updateRegistration(id: string, updates: Partial<Registration>): Promise<boolean> {
    try {
      const headers = await this.authHeaders();
      const res = await this.httpPatch(`${this.baseUrl}/registrations/${id}`, updates, { headers });
      return res.status === 204;
    } catch (error) {
      Log.error(`AppApiService: updateRegistration failed: ${error.message}`);
      return false;
    }
  }

  // ─── Payment Options ────────────────────────────────────────────────────────

  async getPaymentOptions(): Promise<PaymentOption[]> {
    try {
      const headers = await this.authHeaders();
      const res = await this.httpGet(`${this.baseUrl}/payment-options`, { headers });
      return (res.data as PaymentOption[]) ?? [];
    } catch (error) {
      Log.error(`AppApiService: getPaymentOptions failed: ${error.message}`);
      return [];
    }
  }
}

export const AppApiService = new AppApiController();
